import { useState, useEffect } from 'react';
import { OutputFormat } from '@/components/OutputFormatSelector';
import { getCachedOutputFormat } from '@/lib/outputFormatCache';

interface UseOutputFormatArgs {
  initialOutputFormat?: OutputFormat;
  conversationId: string;
  requiresSolidbody?: boolean;
  onOutputFormatChange?: (format: OutputFormat) => void;
}

interface UseOutputFormatResult {
  outputFormat: OutputFormat;
  setOutputFormat: (fmt: OutputFormat) => void;
}

// Kernel-selection (output-format) state + sync effects, extracted verbatim
// from TextAreaChat. Bundles a contiguous run of three hooks — the lazy
// `useState` initializer, the sync-from-prop effect, and the
// requiresSolidbody effect — so the root's observable hook order is
// unchanged when this is called in their place.
export function useOutputFormat({
  initialOutputFormat,
  conversationId,
  requiresSolidbody,
  onOutputFormatChange,
}: UseOutputFormatArgs): UseOutputFormatResult {
  // Kernel selection — local state, initialized in priority order:
  //   1. `initialOutputFormat` prop (passed by ChatSection from conversation.settings)
  //   2. localStorage cache keyed by conversation.id (PromptView writes this at
  //      creation time, so EditorView's mount can recover it instantly even
  //      if the prop pass isn't in place yet)
  //   3. 'mesh' default for brand-new conversations.
  // EditorView only mounts this component AFTER the conversation row has
  // loaded (EditorView shows a spinner while isConversationLoading), so by
  // the time we read the cache, conversation.id is the real ID — not the
  // empty default from ConversationContext.
  const [outputFormat, setOutputFormatLocal] = useState<OutputFormat>(() => {
    if (initialOutputFormat === 'mesh' || initialOutputFormat === 'solidbody') {
      return initialOutputFormat;
    }
    if (conversationId) {
      const cached = getCachedOutputFormat(conversationId);
      if (cached) return cached;
    }
    return 'solidbody';
  });

  const setOutputFormat = (fmt: OutputFormat) => {
    setOutputFormatLocal(fmt);
    onOutputFormatChange?.(fmt);
  };

  // EditorView's conversation row often resolves asynchronously after this
  // component mounts — the URL is known but the row is still loading, so
  // `initialOutputFormat` is `undefined` on first render and only becomes
  // 'mesh' / 'solidbody' once the query lands. `useState` ignores prop
  // changes after the first render, so without this effect the dropdown
  // stays stuck at the default. We only sync FROM the prop when it has a
  // real value — never back to undefined — so a user clicking the dropdown
  // mid-session isn't clobbered by the next render.
  useEffect(() => {
    if (initialOutputFormat && initialOutputFormat !== outputFormat) {
      setOutputFormatLocal(initialOutputFormat);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOutputFormat]);

  useEffect(() => {
    if (requiresSolidbody === true) {
      setOutputFormat('solidbody');
    } else if (requiresSolidbody === false) {
      setOutputFormat('mesh');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setOutputFormat is an inline wrapper re-created each render; adding it to deps would run the effect on every keystroke. The effect is safe because the writes are idempotent.
  }, [requiresSolidbody]);

  return { outputFormat, setOutputFormat };
}
